import * as React from "react";
import {
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Stack,
} from "@mui/material";
import { AuthenticationContext, ImagesContext } from "../Context";
import ChargeForm from "./ChargeForm";
import AmountAlreadyPaid from "./AmountAlreadyPaid";
import SecondaryInsurance from "./SecondaryInsurance";
import AcceptingAssignment from "./AcceptingAssignment";
import PriorAuthorizationNumber from "./PriorAuthorizationNumber";
import ReferringProvider from "./ReferringProvider";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ClaimBuilderHeader from "../patient_encounters/ClaimBuilderHeader";
import { getHeaders } from "../utils/HeaderHelper";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { ChargeLine, Claim } from "./types";
import { formatMonetaryValue } from "../utils/MonetaryFormatHelper";
import { useFlash } from "../FlashContext";

const emptyCharge: ChargeLine = {
  id: null,
  cpt_code: "",
  charge_amt: "",
  units: "",
  modifiers: [],
  diagnosis_codes: [{ code: "", index: 0, id: null }],
  from_date: "",
  to_date: "",
  family_planning: false,
  epsdt: "",
  emergency_services: false,
  place_of_service: "",
  rendering_provider_id: "",
  ren_provider_qual: "",
  ren_provider_npi: "",
  ren_provider_tax: "",
  ndc_code: "",
  ndc_charge: "",
  unit_modifier: "",
};

const emptyClaim: Claim = {
  id: null,
  amount_paid: "",
  prior_authorization_number: "",
  accept_assignment: true,
  opt_out_sec_ins: false,
  claim_number: "",
  claim_type: "N/A",
  require_prior_authorization: false,
  require_additional_provider: false,
  additional_provider_role: "referring",
  claim_template_id: null,
};

const ClaimsBuilderStepOne: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const { patientId, claimId } = useParams();

  const queryParams = new URLSearchParams(location.search);
  const encounterBillingId = queryParams.get("encounterBillingId");
  const selectedClaimTemplateId = queryParams.get("selected_template");

  const images = React.useContext(ImagesContext);
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const [loading, setLoading] = React.useState(false);
  const [claim, setClaim] = React.useState<Claim>({ ...emptyClaim });
  const [existingClaim, setExistingClaim] = React.useState<any>(null);
  const [charges, setCharges] = React.useState<ChargeLine[]>([
    { ...emptyCharge },
  ]);

  const [showAdditionalDetailsSection, seShowAdditionalDetailsSection] =
    React.useState(false);
  const [hasSecondaryInsurance, setHasSecondaryInsurance] =
    React.useState(false);
  const [renderingProviders, setRenderingProviders] = React.useState<any>(null);
  const [encounterBilling, setEncounterBilling] = React.useState<any>(null);
  const [claimTemplate, setClaimTemplate] = React.useState<any>(null);
  const [referringProviderId, setReferringProviderId] = React.useState<
    number | null
  >(null);
  const [placeOfServiceList, setPlaceOfServiceList] = React.useState<any>([]);
  const [showNDCSection, setShowNDCSection] = React.useState(false);
  const [orgPlaceOfService, setOrgPlaceOfService] = React.useState("");

  const totalModifiers = charges.reduce(
    (sum, charge) => sum + charge.modifiers.length,
    0
  );

  const totalDiagnosisCodes = charges.reduce(
    (sum, charge) => sum + charge.diagnosis_codes.length,
    0
  );

  const claimStatus = claim?.claim_status;

  const checkExistingBillingRecordsNew = async () => {
    const encounterId = encounterBillingId;
    if (encounterId) {
      await fetch(
        `/billing/claims/new?encounter_billing_id=${encounterId}&patient_id=${patientId}`,
        {
          method: "GET",
          headers: getHeaders(authenticationSetting.csrfToken),
        }
      )
        .then((result) => result.json())
        .then((result) => {
          if (result.success == false) {
            console.log(result.error);
          } else {
            const encounterBilling = result?.resource.encounter_billing;
            setEncounterBilling(encounterBilling);
            setOrgPlaceOfService(result.resource.organization_place_of_service);
            setHasSecondaryInsurance(
              result.resource.secondary_patient_insurance
            );
            setPlaceOfServiceList(result.resource.place_of_service_list);
            setRenderingProviders(result.resource.rendering_providers);

            const currentUser = authenticationSetting?.currentUser;
            const isCurrentUserQualified =
              currentUser?.provider_npi_number &&
              currentUser?.id_qualifier &&
              currentUser?.id_number;

            const getDefaultRenderingProviderId = (charge: ChargeLine): string => {
              if (charge.rendering_provider_id) {
                return charge.rendering_provider_id;
              }
              return isCurrentUserQualified ? String(currentUser?.id) : "";
            };

            const getDefaultNpi = (charge: ChargeLine): string => {
              if (charge.ren_provider_npi) {
                return charge.ren_provider_npi;
              }
              return isCurrentUserQualified ? currentUser?.provider_npi_number || "" : "";
            };

            const getDefaultIdQualifier = (charge: ChargeLine): string => {
              if (charge.ren_provider_qual) {
                return charge.ren_provider_qual;
              }
              return isCurrentUserQualified ? currentUser?.id_qualifier || "" : "";
            };

            const getDefaultIdNumber = (charge: ChargeLine): string => {
              if (charge.ren_provider_tax) {
                return charge.ren_provider_tax;
              }
              return isCurrentUserQualified ? currentUser?.id_number || "" : "";
            };

            setCharges((prevCharges) =>
              prevCharges.map((charge) => ({
                ...charge,
                from_date: charge.from_date
                  ? charge.from_date
                  : encounterBilling.day_of_encounter,
                to_date: charge.to_date
                  ? charge.to_date
                  : encounterBilling.day_of_encounter,
                rendering_provider_id: getDefaultRenderingProviderId(charge),
                place_of_service: result.resource.organization_place_of_service,
                ren_provider_npi: getDefaultNpi(charge),
                ren_provider_qual: getDefaultIdQualifier(charge),
                ren_provider_tax: getDefaultIdNumber(charge),
              }))
            );
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const handleClaimChange = <K extends keyof Claim>(
    field: K,
    value: Claim[K]
  ) => {
    setClaim((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddCharge = () => {
    if (charges.length < 6) {
      const newCharge = {
        ...emptyCharge,
        from_date: encounterBilling.day_of_encounter,
        to_date: encounterBilling.day_of_encounter,
        place_of_service: orgPlaceOfService,
      };

      setCharges([...charges, newCharge]);
    }
  };

  const handleRemoveCharge = (index: number) => {
    if (index === 0) return;
    const updated = [...charges];
    updated.splice(index, 1);
    setCharges(updated);
  };

  const handleChange = <K extends keyof ChargeLine>(
    index: number,
    field: K,
    value: ChargeLine[K]
  ) => {
    const updated = [...charges];
    updated[index][field] = value;
    setCharges(updated);
  };

  const handleAdditionalDetailsSection = () => {
    seShowAdditionalDetailsSection(!showAdditionalDetailsSection);
  };

  const handleProviderRemoved = () => {
    setClaim((prevClaim) => ({
      ...prevClaim,
      referring_provider_snapshot_attributes: undefined,
    }));
  };

  const fetchClaimTemplate = () => {
    fetch(`/claim_templates/${selectedClaimTemplateId}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          console.log(result.error);
        } else {
          const template = result.resource;
          setClaimTemplate(template);

          setClaim({
            ...claim,
            claim_type: template.title,
            require_prior_authorization:
              template.prior_authorization_number_required,
            require_additional_provider: template.additional_provider_required,
            additional_provider_role: template.additional_provider_role,
            claim_template_id: template.id,
          });

          // Auto-populate charge lines from template
          if (
            template.template_charge_lines &&
            template.template_charge_lines.length > 0
          ) {
            const populatedCharges = template.template_charge_lines.map(
              (templateCharge: any) => {
                const newCharge = {
                  ...emptyCharge,
                  cpt_code: templateCharge.cpt_code || "",
                  units: templateCharge.units || "",
                  ndc_code: templateCharge.ndc_code || "",
                  unit_modifier: templateCharge.unit_modifier || "",
                  from_date: encounterBilling?.day_of_encounter || "",
                  to_date: encounterBilling?.day_of_encounter || "",
                  place_of_service: orgPlaceOfService,
                  modifiers: templateCharge.template_modifiers?.map(
                    (mod: any) => ({
                      code: mod.code,
                      index: mod.index,
                      id: null,
                    })
                  ) || [{ code: "", index: 0, id: null }],
                  diagnosis_codes: templateCharge.template_diagnosis_codes?.map(
                    (diag: any) => ({
                      code: diag.code,
                      index: diag.index,
                      id: null,
                    })
                  ) || [{ code: "", index: 0, id: null }],
                };

                // Pricing will be auto-populated by the ChargeForm component
                // when the CPT or NDC codes are set

                return newCharge;
              }
            );

            setCharges(populatedCharges);
          }
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  React.useEffect(() => {
    if (selectedClaimTemplateId) {
      fetchClaimTemplate();
    }
  }, [selectedClaimTemplateId]);

  const handleProviderSaved = (providerId: number) => {
    setReferringProviderId(providerId);
    fetch(`/referring_provider_snapshots/${providerId}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((response) => response.json())
      .then((data) => {
        setClaim((prevClaim) => ({
          ...prevClaim,
          referring_provider_snapshot_attributes: data,
        }));
      });
  };

  const handleBackToClaimList = () => {
    const encounterBillingUid = encounterBillingId || encounterBilling?.uuid;
    history.push(
      `/patient_reports/${patientId}/patient_encounters/new?encounter_id=${encounterBillingUid}&view_only=1`
    );
  };

  const getClaimInfo = () => {
    if (claimId) {
      getClaimStepOneInfoNew();
    } else {
      checkExistingBillingRecordsNew();
    }
  };

  React.useEffect(() => {
    getClaimInfo();
  }, [claimId, patientId]);

  const getClaimStepOneInfoNew = async () => {
    await fetch(`/billing/claims/${claimId}?patient_id=${patientId}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          console.log(result.error);
        } else {
          const existingClaim = result.resource.claim;

          setExistingClaim(existingClaim);
          setEncounterBilling(result.resource?.encounter_billing);
          setOrgPlaceOfService(result.resource.organization_place_of_service);
          setHasSecondaryInsurance(result.resource.secondary_patient_insurance);
          setPlaceOfServiceList(result.resource.place_of_service_list);
          setRenderingProviders(result.resource.rendering_providers);

          setClaim({
            id: existingClaim.id ?? null,
            amount_paid: existingClaim.amount_paid ?? "",
            prior_authorization_number:
              existingClaim.prior_authorization_number ?? "",
            accept_assignment: existingClaim.accept_assignment ?? true,
            opt_out_sec_ins: existingClaim.opt_out_sec_ins ?? false,
            claim_number: existingClaim.claim_number ?? "",
            referring_provider_snapshot_attributes:
              existingClaim.referring_provider_snapshot ?? undefined,
            claim_status: existingClaim.claim_status,
            claim_type: existingClaim?.claim_type,
            require_prior_authorization:
              existingClaim.require_prior_authorization,
            require_additional_provider:
              existingClaim.require_additional_provider,
            additional_provider_role: existingClaim.additional_provider_role,
            claim_template_id: existingClaim.claim_template_id,
          });

          setCharges(
            existingClaim?.charge_lines?.map((line: any) => ({
              id: line.id ?? null,
              cpt_code: line.cpt_code ?? "",
              charge_amt: formatMonetaryValue(line.charge_amt) || "",
              units: line.units ?? "",
              modifiers: line.modifiers?.map((m: any, index: number) => ({
                id: m.id ?? null,
                code: m.code,
                index,
              })) ?? [{ code: "", index: 0, id: null }],
              diagnosis_codes: line.diagnosis_codes?.map(
                (d: any, index: number) => ({
                  id: d.id ?? null,
                  code: d.code,
                  index,
                })
              ) ?? [{ code: "", index: 0, id: null }],
              from_date: line.from_date ?? "",
              to_date: line.to_date ?? "",
              family_planning: line.family_planning ?? false,
              epsdt: line.epsdt ?? "",
              emergency_services: line.emergency_services ?? false,
              ndc_code: line.ndc_code,
              ndc_charge: formatMonetaryValue(line.ndc_charge) || "",
              unit_modifier: line.unit_modifier,
              place_of_service:
                line.place_of_service ||
                result.resource.organization_place_of_service,
              rendering_provider_id: line.ren_provider_id ?? "",
              ren_provider_qual: line.ren_provider_qual ?? "",
              ren_provider_npi: line.ren_provider_npi ?? "",
              ren_provider_tax: line.ren_provider_tax ?? "",
            })) ?? [{ ...emptyCharge }]
          );
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const saveClaim = async () => {
    const endpoint = claim?.id
      ? `/billing/claims/${claim.id}`
      : "/billing/claims";
    const method = claim?.id ? "PUT" : "POST";

    // For existing claims, we need to track charges that were removed
    const originalChargeIds =
      existingClaim?.charge_lines?.map((line: any) => line.id) || [];
    const currentChargeIds = charges.map((charge) => charge.id).filter(Boolean);

    const chargesToDestroy = originalChargeIds.filter(
      (id) => !currentChargeIds.includes(id)
    );

    // Prepare charge lines data
    const chargeLinesData = charges.map((charge) => {
      const chargeLineData: any = {
        cpt_code: charge.cpt_code,
        charge_amt: formatMonetaryValue(charge.charge_amt),
        units: charge.units,
        from_date: charge.from_date,
        to_date: charge.to_date,
        ren_provider_qual: charge.ren_provider_qual,
        ren_provider_npi: charge.ren_provider_npi,
        ren_provider_tax: charge.ren_provider_tax,
        place_of_service: charge.place_of_service,
        family_planning: charge.family_planning,
        epsdt: charge.epsdt,
        emergency_services: charge.emergency_services,
        ndc_code: charge.ndc_code,
        ndc_charge: formatMonetaryValue(charge.ndc_charge),
        unit_modifier: charge.unit_modifier,
        modifiers_attributes: charge.modifiers.map((modifier: any) => ({
          id: modifier.id || undefined,
          code: modifier.code,
          index: modifier.index,
          _destroy: modifier.code ? false : true,
        })),
        diagnosis_codes_attributes: charge.diagnosis_codes.map(
          (diagnosis: any) => ({
            id: diagnosis.id || undefined,
            code: diagnosis.code,
            index: diagnosis.index,
            _destroy: diagnosis.code ? false : true,
          })
        ),
      };

      if (charge.id) {
        chargeLineData.id = charge.id;
        // Mark for destruction if this charge was removed
        chargeLineData._destroy = chargesToDestroy.includes(charge.id);
      }

      return chargeLineData;
    });

    // For deleted charges that aren't in the current list
    chargesToDestroy.forEach((id) => {
      if (!charges.some((c) => c.id === id)) {
        chargeLinesData.push({
          id: id,
          _destroy: true,
        });
      }
    });

    const response = await fetch(endpoint, {
      method,
      headers: {
        ...getHeaders(authenticationSetting.csrfToken),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        encounter_uuid: encounterBillingId,
        claim: {
          encounter_uuid: encounterBillingId,
          ...claim,
          charge_lines_attributes: chargeLinesData,
        },
        referring_provider_snapshot_id: referringProviderId ?? null,
      }),
    });

    const data = await response.json();
    return data;
  };

  const handleSaveDraft = async () => {
    setLoading(true);
    try {
      const data = await saveClaim();

      if (data.success) {
        showToast({
          title: "Claim Saved",
          text: claim?.id
            ? "Claim updated successfully"
            : "Claim saved as draft",
          type: "success",
        });

        const savedClaimId = data.resource.id || claim?.id;

        setExistingClaim(data.resource);

        if (savedClaimId) {
          history.replace({
            pathname: `/patient_reports/${patientId}/claims_builder/${savedClaimId}`,
            search: location.search,
          });
        }
      } else {
        showToast({
          title: "Error",
          text: data.error ?? "Something went wrong",
          type: "error",
        });
      }
    } catch (error) {
      showToast({
        title: "Error",
        text: "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getChargeTitle = (i: number) =>
    `Charge Line ${["One", "Two", "Three", "Four", "Five", "Six"][i]}`;

  const handleContinue = async () => {
    event.preventDefault();

    const currentUser = authenticationSetting?.currentUser;

    if (
      claim.require_prior_authorization &&
      !claim.prior_authorization_number
    ) {
      showToast({
        title: "Prior Authorization Number Required",
        text: "Prior Authorization Number is required",
        type: "error",
      });

      return;
    }

    if (
      claim.require_additional_provider &&
      !claim.referring_provider_snapshot_attributes
    ) {
      showToast({
        title: "Additional Provider Required",
        text: "Additional provider is required",
        type: "error",
      });

      return;
    }

    const errors: string[] = [];

    charges?.forEach((charge, index) => {
      const missingFields: string[] = [];

      if (
        !charge.cpt_code ||
        charge.cpt_code.length < 3 ||
        charge.cpt_code.length > 5
      ) {
        missingFields.push("CPT code must be between 3 to 5 characters");
      }

      if (!charge.charge_amt) {
        missingFields.push("Charge amount is required");
      }

      if (!charge.units) {
        missingFields.push("Units is required");
      }

      if (
        charge.diagnosis_codes.length === 0 ||
        !charge.diagnosis_codes[0].code
      ) {
        missingFields.push("At least one diagnosis code required");
      } else {
        const invalidCodes = charge.diagnosis_codes.filter(
          (diagnosisCode) =>
            !diagnosisCode.code ||
            diagnosisCode.code.length < 3 ||
            diagnosisCode.code.length > 7
        );
        if (invalidCodes.length > 0) {
          missingFields.push(
            "Diagnosis codes must be between 33 to 7 characters"
          );
        }
      }

      if (showNDCSection) {
        if (!charge.ndc_code || charge.ndc_code.length !== 11) {
          missingFields.push("NDC code must be 11 characters");
        }

        if (!charge.ndc_charge) {
          missingFields.push("NDC Charge is required");
        }

        if (!charge.unit_modifier) {
          missingFields.push("Unit Modifier is required");
        }
      }

      const isCurrentUserQualified =
        currentUser?.provider_npi_number &&
        currentUser?.id_qualifier &&
        currentUser?.id_number;

      let provider = renderingProviders?.find(
        (p) => String(p.id) === String(charge.rendering_provider_id)
      );

      if (!provider && isCurrentUserQualified) {
        provider = currentUser;
      }

      const hasSelectedProvider = !!provider;
      const userNeedsToSelectProvider = !isCurrentUserQualified && !charge.rendering_provider_id;

      if (userNeedsToSelectProvider) {
        missingFields.push(
          "You are not qualified to be a rendering provider. Please select a different provider."
        );
      } else if (hasSelectedProvider) {
        const isProviderQualified =
          provider.provider_npi_number &&
          provider.id_qualifier &&
          provider.id_number;

        if (!isProviderQualified) {
          missingFields.push(
            "The selected rendering provider is not qualified. Please choose a provider with NPI, ID Qualifier, and Rendering Provider ID #."
          );
        }
      } else {
        missingFields.push(
          "A rendering provider must be selected. Please choose a qualified provider."
        );
      }

      if (missingFields.length > 0) {
        errors.push(
          `${getChargeTitle(index)} is invalid: ${missingFields.join(", ")}`
        );
      }
    });

    if (errors.length > 0) {
      showToast({
        title: "Validation Errors",
        text: errors.join("\n"),
        type: "error",
      });
      return;
    }

    const data = await saveClaim();

    const savedClaimId = data.resource.id;

    setExistingClaim(data.resource);

    if (savedClaimId) {
      history.replace({
        pathname: `/patient_reports/${patientId}/claims_builder/${savedClaimId}}`,
        search: location.search,
      });
      history.push(
        `/patient_reports/${patientId}/claims_builder_review/${savedClaimId}?encounterBillingId=${encounterBillingId}`
      );
    } else {
      showToast({
        title: "Error",
        text: "Something went wrong",
        type: "error",
      });
    }
  };

  const handleClaimReview = () => {
    history.push(
      `/patient_reports/${patientId}/claims_builder_review/${claimId}`
    );
  };

  return (
    <Stack>
      <ClaimBuilderHeader
        claimNumber={claim?.claim_number}
        claimStatus={claimStatus}
        claimType={claim?.claim_type}
        handleBackToClaimList={handleBackToClaimList}
        handleSaveDraft={handleSaveDraft}
        handleContinue={handleContinue}
        handleClaimReview={handleClaimReview}
      />

      <Grid container className="panel-container" borderRadius={"4px"}>
        <Grid item xs={12}>
          <Grid container className="panel-show-container">
            <Grid container className="panel-information-container">
              <Grid container direction="row" className="admin-header">
                <Grid item xs={12} className="box-header">
                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    paddingX={3}
                    paddingY={1}
                    alignItems={"center"}
                    display={"flex"}
                  >
                    <Grid item>
                      <p className="panel-header">Charge Code(s)</p>
                    </Grid>
                    {claimStatus !== "submitted" &&
                      claimStatus !== "pending_submission" && (
                        <div className="row">
                          <Link
                            className="action-link row"
                            onClick={handleAddCharge}
                          >
                            <img
                              src={images.add_icon}
                              alt="Add Charge"
                              className="action-icon-image"
                              style={{ width: 31.466, maxHeight: 32 }}
                            />
                            <span className="app-user-text disable-pointer">
                              Add Charge
                            </span>
                          </Link>
                        </div>
                      )}
                  </Stack>
                </Grid>
              </Grid>
              <Box
                className="general-patient-info-container"
                sx={{ paddingBottom: "0px !important" }}
              >
                {charges.map((charge, index) => (
                  <ChargeForm
                    key={index + 1}
                    index={index}
                    charge={charge}
                    onChange={handleChange}
                    onRemove={handleRemoveCharge}
                    encounterBilling={encounterBilling}
                    totalModifiers={totalModifiers}
                    totalDiagnosisCodes={totalDiagnosisCodes}
                    renderingProviders={renderingProviders}
                    placeOfServiceList={placeOfServiceList}
                    claimStatus={claimStatus}
                    showNDCSection={showNDCSection}
                    setShowNDCSection={setShowNDCSection}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid container className="panel-container" borderRadius={"4px"}>
        <Grid item xs={12}>
          <Grid container className="panel-show-container">
            <Grid container className="panel-information-container">
              <Grid container direction="row" className="admin-header">
                <Grid item xs={12} className="box-header">
                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    paddingX={3}
                    paddingY={1}
                    alignItems={"center"}
                    display={"flex"}
                  >
                    <Grid item>
                      <p className="panel-header">Additional Claim Details</p>
                    </Grid>
                    <IconButton onClick={handleAdditionalDetailsSection}>
                      {showAdditionalDetailsSection ? (
                        <ArrowDropUpIcon />
                      ) : (
                        <ArrowDropDownIcon />
                      )}
                    </IconButton>
                  </Stack>
                </Grid>
              </Grid>
              {showAdditionalDetailsSection && (
                <Grid
                  container
                  wrap="nowrap"
                  className="general-patient-info-container"
                >
                  <Grid item xs={2.4}>
                    <AmountAlreadyPaid
                      amountPaid={claim.amount_paid}
                      onChange={(val) => handleClaimChange("amount_paid", val)}
                      claimStatus={claimStatus}
                    />
                  </Grid>
                  <Grid item xs={2.4} sx={{ ml: 2 }}>
                    <SecondaryInsurance
                      optOutSecIns={claim.opt_out_sec_ins}
                      onChange={(val) =>
                        handleClaimChange("opt_out_sec_ins", val)
                      }
                      hasSecondaryInsurance={hasSecondaryInsurance}
                      claimStatus={claimStatus}
                    />
                  </Grid>
                  <Grid item xs={2.4} sx={{ ml: 2 }}>
                    <AcceptingAssignment
                      acceptAssignment={claim.accept_assignment}
                      onChange={(val) =>
                        handleClaimChange("accept_assignment", val)
                      }
                      claimStatus={claimStatus}
                    />
                  </Grid>
                  <Grid item xs={2.4} sx={{ ml: 2 }}>
                    <PriorAuthorizationNumber
                      priorAuthorizationNumber={
                        claim.prior_authorization_number
                      }
                      onChange={(val) =>
                        handleClaimChange("prior_authorization_number", val)
                      }
                      claimStatus={claimStatus}
                      isRequired={claim.require_prior_authorization}
                    />
                  </Grid>
                  <Grid item xs={2.4} sx={{ ml: 2 }}>
                    <ReferringProvider
                      renderingProviders={renderingProviders}
                      claim={claim}
                      onProviderRemoved={handleProviderRemoved}
                      handleProviderSaved={handleProviderSaved}
                      claimStatus={claimStatus}
                      isRequired={claim.require_additional_provider}
                      providerRole={claim.additional_provider_role}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress />
      </Backdrop>
    </Stack>
  );
};

export default ClaimsBuilderStepOne;
